import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AISummary } from "../components/AISummary";
import { 
  Target, 
  Plus,
  Trash2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

const GOAL_CATEGORIES = [
  { value: "savings", label: "Savings" },
  { value: "emergency", label: "Emergency Fund" },
  { value: "vacation", label: "Vacation" },
  { value: "home", label: "Home Down Payment" },
  { value: "car", label: "Car" },
  { value: "education", label: "Education" },
  { value: "retirement", label: "Retirement" },
  { value: "other", label: "Other" },
];

const STATUS_CONFIG = {
  achieved: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-100 dark:bg-emerald-900/30" },
  on_track: { icon: Target, color: "text-blue-600", bg: "bg-blue-100 dark:bg-blue-900/30" },
  overdue: { icon: AlertTriangle, color: "text-red-600", bg: "bg-red-100 dark:bg-red-900/30" },
};

export const GoalsPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGoal, setNewGoal] = useState({ name: "", target_amount: "", target_date: "", category: "savings" });
  const [creating, setCreating] = useState(false);

  const fetchGoals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/goals");
      setGoals(response.data);
    } catch (error) {
      console.error("Failed to fetch goals:", error);
      toast.error("Failed to load goals");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const handleCreateGoal = async () => {
    if (!newGoal.name || !newGoal.target_amount || !newGoal.target_date) {
      toast.error("Please fill all fields");
      return;
    }
    
    setCreating(true);
    try {
      await api.post("/goals", {
        name: newGoal.name,
        target_amount: parseFloat(newGoal.target_amount),
        target_date: newGoal.target_date,
        category: newGoal.category
      });
      toast.success("Goal created!");
      setShowCreateModal(false);
      setNewGoal({ name: "", target_amount: "", target_date: "", category: "savings" });
      fetchGoals();
    } catch (error) {
      toast.error("Failed to create goal");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      await api.delete(`/goals/${goalId}`);
      toast.success("Goal deleted");
      setGoals(prev => prev.filter(g => g.id !== goalId));
    } catch (error) {
      toast.error("Failed to delete goal");
    }
  };

  const formatCurrency = (amount) => {
    const currency = user?.currency || "CAD";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-CA", {
      month: "short",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="goals-loading">
        <div className="flex justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-48 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  // Compute totals
  var totalSaved = goals.reduce(function(s,g){return s + (g.current_amount||0);},0);
  var totalTarget = goals.reduce(function(s,g){return s + (g.target_amount||0);},0);
  var totalPct = totalTarget > 0 ? Math.round(totalSaved/totalTarget*100) : 0;

  return (
    <div data-testid="goals-page">
      {/* Header */}
      <div className="phead">
        <div><h1>Goals</h1><div className="sub">Save for what matters, one week at a time</div></div>
        <div className="actions"><button className="btn btn-p" onClick={() => setShowCreateModal(true)} data-testid="create-goal-btn"><Plus size={16} stroke="#F4EFE4" /> New goal</button></div>
      </div>

      {/* AI Summary */}
      <AISummary page="goals" />

      {/* Stats */}
      <div className="card stats" style={{marginBottom:24}}>
        <div className="st"><div className="lab">Total saved</div><div className="v" style={{color:"#3F7D52"}}>{formatCurrency(totalSaved)}</div><div className="sm">across {goals.length} goal{goals.length!==1?"s":""}</div></div>
        <div className="st"><div className="lab">Target</div><div className="v">{formatCurrency(totalTarget)}</div><div className="sm">{totalPct}% of the way</div></div>
        <div className="st"><div className="lab">Remaining</div><div className="v">{formatCurrency(Math.max(0,totalTarget-totalSaved))}</div></div>
      </div>

      {goals.length === 0 ? (
        <div className="card" style={{padding:72,textAlign:"center"}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
            <div style={{width:56,height:56,borderRadius:16,background:"#DCEAD8",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:8}}><Target size={26} stroke="#14432B" /></div>
            <div className="serif" style={{fontSize:23,fontWeight:600}}>No goals yet</div>
            <div style={{maxWidth:360,color:"#7d7d70"}}>Set your first financial goal to start tracking progress.</div>
            <button className="btn btn-p" style={{marginTop:12}} onClick={() => setShowCreateModal(true)}><Plus size={16} stroke="#F4EFE4" /> Create your first goal</button>
          </div>
        </div>
      ) : (
        <div className="grid" style={{gap:16}}>
          {goals.map((goal) => {
            var pct = goal.target_amount > 0 ? Math.round(goal.current_amount / goal.target_amount * 100) : 0;
            var ringColor = pct >= 80 ? "#C68A3C" : pct >= 50 ? "#7ED957" : "#3F7D52";
            var barColor = pct >= 100 ? "#C68A3C" : pct >= 50 ? "#7ED957" : "#3F7D52";
            return (
              <div key={goal.id} className="card" style={{padding:"26px 28px",display:"flex",gap:22,alignItems:"center",cursor:"pointer"}} onClick={function(){setSelectedGoal(goal);}} data-testid={"goal-"+goal.id}>
                <div className="gring" style={{background:"conic-gradient("+ringColor+" 0turn "+(pct/100)+"turn, #eee9dd "+(pct/100)+"turn 1turn)"}}><b>{pct}%</b></div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
                    <div className="ai2" style={{width:34,height:34}}><Target size={17} stroke="#3F7D52" /></div>
                    <b style={{fontSize:18}}>{goal.name}</b>
                  </div>
                  <div className="muted" style={{fontSize:14,margin:"2px 0 12px"}}>{formatCurrency(goal.current_amount)} of {formatCurrency(goal.target_amount)}</div>
                  <div className="bar"><i style={{width:Math.min(pct,100)+"%",background:barColor}} /></div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Financial Goal</DialogTitle>
            <DialogDescription>
              Set a SMART goal - Specific, Measurable, Achievable, Relevant, Time-bound
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Goal Name</Label>
              <Input
                placeholder="e.g., Emergency Fund, Vacation to Hawaii"
                value={newGoal.name}
                onChange={(e) => setNewGoal(prev => ({ ...prev, name: e.target.value }))}
                data-testid="goal-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={newGoal.category} 
                onValueChange={(v) => setNewGoal(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger data-testid="goal-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOAL_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Target Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  placeholder="10000"
                  value={newGoal.target_amount}
                  onChange={(e) => setNewGoal(prev => ({ ...prev, target_amount: e.target.value }))}
                  className="pl-7"
                  min={0}
                  data-testid="goal-amount-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Target Date</Label>
              <Input
                type="date"
                value={newGoal.target_date}
                onChange={(e) => setNewGoal(prev => ({ ...prev, target_date: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                data-testid="goal-date-input"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button className="flex-1 rounded-full" onClick={handleCreateGoal} disabled={creating} data-testid="save-goal-btn">
                {creating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Goal"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
